import { FC, useContext, useEffect, useRef, useState } from "react";
import { SxProps, Stack, Link, Box, Chip, ListItemIcon, MenuItem, styled, CircularProgress } from "@mui/material";

import Activity from "../../Activity/activity";
import SettingsIcon from "@mui/icons-material/Settings";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import OfflineBoltIcon from "@mui/icons-material/OfflineBolt";
import MoreMenu from "../../Misc/MoreMenu";
import PanelHeader, { PanelHeaderProps } from "../../Panels/PanelHeader/panel-header";
import { activitiesNotifier, cacheService, logService } from "../../../Core/Api/services";
import Typography from "@mui/material/Typography";
import useDidMountEffect from "../../../Utils/Hooks/useComponentDidMountEffect";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { EventNotifierOperation } from "../../../Core/Api/notifier-client";
import { useDispatch, useSelector } from "react-redux";
import {
  AddNotificationToState,
  GetNotifications,
  IsLoadedNotifications,
  SetNotificationsLoaded,
  StoreInitialNotificationState,
} from "../../../Redux/Reducers/redux-slice-notifications";
import { ModalContext } from "../../Modals";
import ModalUserSettings, { ModalUserSettingsElementProps } from "../../Modals/ModalsUserSettings/modal-user-settings";
import DrawerWrapper from "../../Drawers/Drawer/drawer-wrapper";
import { useTranslation } from "react-i18next";
export interface DrawerProps {
  sx?: SxProps;
  width?: number;
  open: boolean;
  onClose?: React.MouseEventHandler;
  onGetActivitiesCallback: (isErrorFromChild: boolean, activitiesNotReadFromChild: boolean) => void;
}

const NotificationDrawerFooter = styled("div")(({ theme }) => ({
  position: "sticky",
  bottom: "0",
  left: "0",
  right: "0",
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.backgroundAlpha.border}`,
  padding: "16px",
  textAlign: "center",
  marginTop: "auto",
  zIndex: 1,
}));

const PanelNotification: FC<DrawerProps> = (props) => {
  const { t } = useTranslation();
  const pageCount = useRef(0);
  const pageSize = 9;
  const enqueueCustomSnackbar = useCustomSnackbar();
  const dispatch = useDispatch();
  const activities = useSelector(GetNotifications);
  const isLoaded = useSelector(IsLoadedNotifications);
  const activityTypesWhereSnakbar: string[] = ["JobCompleted", "JobFailed", "JobAbort"];
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [snackbarEnabled, setSnackbarEnabled] = useState(cacheService.snackbarEvents);

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  // Initialize activities list
  useEffect(() => {
    if (isLoaded) return;

    logService.ListSessionUserActivities(0, pageSize).then((data) => {
      dispatch(StoreInitialNotificationState(data));
      dispatch(SetNotificationsLoaded(true));
    });
  }, [isLoaded]);

  // SignalR
  useEffect(() => {
    activitiesNotifier.Connect().then(function (result) {
      if (activitiesNotifier.connection)
        activitiesNotifier.connection.on("ActivityMessage", (data) => {
          switch (data.op) {
            case EventNotifierOperation.Create:
              if (cacheService.GetSnackbarEventsEnable() && activityTypesWhereSnakbar.includes(data.data.type)) {
                enqueueCustomSnackbar(
                  data.data.typeExp + " #" + data.data.resId,
                  data.data.type.toString().includes("Fail") ? "warning" : "primary",
                  false,
                  null,
                  data.data.msg ? data.data.msg : null
                );
              }

              // Here add notification to panel
              dispatch(AddNotificationToState(logService.GetMappedActivity(data.data)));
              break;
            case EventNotifierOperation.Update:
              break;
            case EventNotifierOperation.Delete:
              break;
          }
        });
    });

    return function cleanup() {
      // Disconnect hub when dismounted
      activitiesNotifier.Disconnect().then(function () {});
    };
  }, []);

  // Handle badge on parent
  useDidMountEffect(() => {
    if (activities.length > 0) {
      // code below is a bit longer but optimized : if there is an error we do not search for an unread activity as we are doint it
      // if there is an error activity not read
      if (activities.some((x) => x.read === false && x.error === true)) {
        props.onGetActivitiesCallback(true, true);
      } else if (activities.some((x) => x.read === false)) {
        // else if there is an activity not read
        props.onGetActivitiesCallback(false, true);
      } else {
        props.onGetActivitiesCallback(false, false);
      }
    }
  }, [activities]);

  // Handle setAllReadButton
  const setAllRead = () => {
    // if no activities has to be set to read, we don't make the call and re-render
    if (activities.some((x) => x.read === false))
      logService.ReadAllActivities().then(() => {
        activities?.forEach((x) => (x.read = true));
        const newActivities = [...activities];
        // setActivities(newActivities);
        dispatch(StoreInitialNotificationState(newActivities));
      });
  };

  const openNotifSettings = () => {
    if (!openModal) return;
    openModal<ModalUserSettingsElementProps>(ModalUserSettings);
  };

  const toggleSnackbar = () => {
    cacheService.ToggleSnackbarEventsEnable();
    setSnackbarEnabled((prev) => !prev);
  };

  // Handle load more button
  const loadMore = () => {
    pageCount.current++;
    setIsLoadingMore(true);

    logService.ListSessionUserActivities(pageCount.current, pageSize).then((data) => {
      const newActivities = [...activities];
      data.forEach((x) => (x.id += newActivities.length));
      newActivities.push(...data);
      dispatch(StoreInitialNotificationState(newActivities));
      setIsLoadingMore(false);
    });
  };

  return (
    <DrawerWrapper
      open={props.open}
      onClose={props.onClose}
      header={
        <PanelHeader
          title={t("panel:title.recentNotifications")}
          onClose={props.onClose as PanelHeaderProps["onClose"]}
          rightContent={
            <MoreMenu>
              <MenuItem onClick={setAllRead}>
                <ListItemIcon>
                  <DoneAllOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {t("menu:item.markAsRead")}
              </MenuItem>
              <MenuItem onClick={openNotifSettings}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                {t("menu:item.notificationsSettings")}
              </MenuItem>
              <MenuItem onClick={toggleSnackbar}>
                <ListItemIcon>
                  <OfflineBoltIcon fontSize="small" />
                </ListItemIcon>
                {t("menu:item.snackbarEventsToggle", { context: snackbarEnabled ? "enabled" : "disabled" })} -{" "}
                {t("menu:item.clickToTurn", { context: snackbarEnabled ? "off" : "on" })}
              </MenuItem>
            </MoreMenu>
          }
        />
      }
    >
      <Box>
        <Stack direction="row" spacing={1} mb={3} justifyContent="space-between">
          <Chip label={t("common:all")} size="large" color="primary" variant="outlined" />
          <Chip label={`@${t("common:mentions")}`} size="large" variant="tag" onClick={() => console.log("clic")} />
          <Chip label={t("button:actionsNeeded")} size="large" variant="tag" onClick={() => console.log("clic")} />
          <Chip label={t("common:discussions")} size="large" variant="tag" onClick={() => console.log("clic")} />
        </Stack>
        {activities.length > 0 ? (
          activities?.map((activity) => <Activity {...activity} sx={{ mb: 2 }} key={activity.id} />)
        ) : (
          <Typography>{t("common:noActivities")}</Typography>
        )}
      </Box>
      <NotificationDrawerFooter>
        <Link onClick={loadMore} underline="hover" variant="body1" color="primary.text" component="button">
          {isLoadingMore ? <CircularProgress size={20} /> : t("button:loadMore")}
        </Link>
      </NotificationDrawerFooter>
    </DrawerWrapper>
  );
};

export default PanelNotification;
